import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { marked } from "marked";
import DOMPurify from "dompurify";
import api, { mediaUrl } from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { AboutModal, ScheduleModal } from "../components/Modals";
import { AudioPlayer, VideoPlayer } from "../components/MediaPlayer";
import BlockchainBadge from "../components/BlockchainBadge";
import AdSlot from "../components/AdSlot";
import ShareButtons from "../components/ShareButtons";
import DonationWidget from "../components/DonationWidget";
import RelatedPosts from "../components/RelatedPosts";
import { JsonLd, articleSchema } from "../components/JsonLd";
import { trackPageView } from "../lib/analytics";
import { prepareArticleContent } from "../lib/articleReaders";
import ArticleBodyPager from "../components/ArticleBodyPager";
import "../reader.css";

marked.setOptions({ gfm: true, breaks: true });
const PdfReader = React.lazy(() => import("../components/PdfReader"));

function renderContent(raw) {
  if (!raw) return "";
  // TipTap outputs HTML (starts with an HTML tag). Older seed posts may still be markdown.
  const looksHtml = /^\s*<(h[1-6]|p|div|ul|ol|blockquote|pre|figure|img|iframe|hr|strong|em)/i.test(raw);
  const html = looksHtml ? raw : marked.parse(raw);
  return DOMPurify.sanitize(html, {
    ADD_TAGS: ["iframe"],
    ADD_ATTR: ["allow", "allowfullscreen", "frameborder", "scrolling", "target"],
  });
}

export default function Article() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/posts/slug/${encodeURIComponent(slug)}`)
      .then((r) => {
        setPost(r.data);
        trackPageView(`/articles/${slug}`, slug);
        // fetch related
        api.get(`/posts/${r.data.id}/related`, { params: { limit: 4 } })
          .then((rr) => setRelated(rr.data.items || []))
          .catch(() => {});
      })
      .catch((e) => setError(e.response?.status === 404 ? "not found" : "error loading"))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!post) return;
    document.title = post.seo_title || `${post.title} | xaelserpent`;
    const setMeta = (sel, attr, content) => {
      let el = document.head.querySelector(sel);
      if (!el) {
        el = document.createElement("meta");
        if (sel.includes("property")) el.setAttribute("property", sel.match(/"([^"]+)"/)[1]);
        else if (sel.includes("name")) el.setAttribute("name", sel.match(/"([^"]+)"/)[1]);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, content);
    };
    const desc = post.seo_description || post.excerpt || "";
    const imageUrl = post.cover_image_url ? mediaUrl(post.cover_image_url) : `${window.location.origin}/yellow.png`;
    const ogType = post.video_url ? "video.other" : "article";
    const cardType = post.video_url ? "player" : "summary_large_image";
    setMeta('meta[name="description"]', "content", desc);
    setMeta('meta[property="og:title"]', "content", post.seo_title || post.title);
    setMeta('meta[property="og:description"]', "content", desc);
    setMeta('meta[property="og:image"]', "content", imageUrl);
    setMeta('meta[property="og:url"]', "content", window.location.href);
    setMeta('meta[property="og:type"]', "content", ogType);
    setMeta('meta[property="og:site_name"]', "content", "xaelserpent.com");
    setMeta('meta[property="og:image:width"]', "content", "1200");
    setMeta('meta[property="og:image:height"]', "content", "630");
    if (post.video_url) {
      setMeta('meta[property="og:video"]', "content", mediaUrl(post.video_url));
      setMeta('meta[property="og:video:type"]', "content", "video/mp4");
    }
    setMeta('meta[name="twitter:card"]', "content", cardType);
    setMeta('meta[name="twitter:title"]', "content", post.seo_title || post.title);
    setMeta('meta[name="twitter:description"]', "content", desc);
    setMeta('meta[name="twitter:image"]', "content", imageUrl);
  }, [post]);

  const html = post?.content ? renderContent(post.content) : "";
  const articleContent = useMemo(() => {
    try {
      return prepareArticleContent(html);
    } catch {
      return { html, pdfs: [] };
    }
  }, [html]);

  const published = post?.published_at
    ? new Date(post.published_at).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })
    : null;

  const otsStatus = (post?.opentimestamps_status || "unreachable").toLowerCase();
  const otsStatusColor = ["verified", "confirmed", "submitted", "pending"].includes(otsStatus)
    ? (otsStatus === "verified" || otsStatus === "confirmed" ? "#6bff8a" : "#ffde16")
    : "#ff8a80";

  return (
    <div className="App">
      <Header />

      <main className="xs-page" data-testid="article-page">
        <div className="xs-container">
          <div style={{ padding: "0 4px" }}>
            <Link to="/" style={{ fontSize: "0.9em" }} data-testid="back-home-link">← back to home</Link>
          </div>

          {loading && <div style={{ padding: 40, opacity: 0.6 }} data-testid="article-loading">loading…</div>}

          {error && (
            <div style={{ padding: 40, textAlign: "center" }} data-testid="article-error">
              <h2 style={{ color: "#ffde16" }}>{error}</h2>
              <button className="xs-btn" onClick={() => nav("/")}>return home</button>
            </div>
          )}

          {post && (
            <>
              <JsonLd data={articleSchema(post, typeof window !== "undefined" ? window.location.origin : "")} />

              <article className="xs-article" data-testid="article-content">
                <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", marginBottom: 8 }}>
                  {post.tags?.map((t) => (
                    <span key={t} className="xs-tag" style={{ color: "#ffde16" }}>#{t}</span>
                  ))}
                  <BlockchainBadge
                    hash={post.blockchain_hash}
                    status={post.opentimestamps_status}
                    verificationUrl={post.opentimestamps_verification_url}
                  />
                </div>

                <h1>{post.title}</h1>

                {published && (
                  <div style={{ fontSize: "0.85em", opacity: 0.75, marginBottom: 10 }}>
                    published {published}  •  {post.view_count || 0} view{post.view_count === 1 ? "" : "s"}
                  </div>
                )}

                {post.excerpt && (
                  <p style={{ color: "#ffde16", opacity: 0.95, fontStyle: "italic", fontSize: "1.05em" }}>
                    {post.excerpt}
                  </p>
                )}

                {post.cover_image_url && (
                  <img src={mediaUrl(post.cover_image_url)} alt={post.title} className="xs-cover" />
                )}

                {post.video_url && <VideoPlayer src={post.video_url} poster={post.cover_image_url} />}
                {post.audio_url && <AudioPlayer src={post.audio_url} title={post.title} />}

                <AdSlot slot="article-top" />

                <ArticleBodyPager html={articleContent.html} />

                {articleContent.pdfs.map((pdf) => (
                  <React.Suspense
                    key={pdf.url}
                    fallback={<div className="xs-pdf-loading" aria-live="polite">opening PDF…</div>}
                  >
                    <PdfReader url={pdf.url} label={pdf.label} />
                  </React.Suspense>
                ))}

                <ShareButtons post={post} />

                {post.revisions?.length > 0 && (
                  <div style={{ marginTop: 18 }}>
                    <button type="button" className="xs-btn ghost" onClick={() => setHistoryOpen((open) => !open)}>
                      {historyOpen ? "hide edit history" : "edit history"}
                    </button>
                    {historyOpen && (
                      <div style={{ marginTop: 10, padding: 12, border: "1px solid rgba(255,222,22,.35)", borderRadius: 8 }}>
                        {post.revisions.map((revision) => (
                          <div key={revision.revision} style={{ padding: "8px 0", borderBottom: "1px dashed rgba(255,222,22,.15)" }}>
                            <strong>revision {revision.revision}</strong>{" "}
                            <span style={{ opacity: 0.7 }}>{revision.timestamp}</span>{" "}
                            <span>({revision.status})</span>
                            <div style={{ wordBreak: "break-all", fontSize: "0.8em", marginTop: 4 }}>{revision.hash}</div>
                            <div style={{ marginTop: 4 }}>{revision.change_note || (revision.revision === 1 ? "original stamp" : "edited")}</div>
                            {revision.proof_url && <a href={mediaUrl(revision.proof_url)} download>download .ots</a>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <hr />

                <div style={{ fontSize: "0.8em", opacity: 0.75, lineHeight: 1.6 }}>
                  <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                    <strong>blockchain hash (sha256):</strong>
                    <BlockchainBadge
                      hash={post.blockchain_hash}
                      status={post.opentimestamps_status}
                      verificationUrl={post.opentimestamps_verification_url}
                      compact
                    />
                  </div>
                  <div style={{ marginTop: 6 }}>
                    <strong>opentimestamps:</strong>{" "}
                    {post.opentimestamps_verification_url ? (
                      <>
                        <a href={post.opentimestamps_verification_url} target="_blank" rel="noreferrer">
                          verify on opentimestamps.org ↗
                        </a>{" "}
                        (<a href={`/api/posts/${post.id}/proof.ots`} target="_blank" rel="noreferrer">.ots proof</a>)
                        {" — "}status: <span style={{ color: otsStatusColor, fontWeight: 700 }}>{post.opentimestamps_status || "unreachable"}</span>
                      </>
                    ) : (
                      <span style={{ color: "#ff8a80" }}>status: unreachable</span>
                    )}
                  </div>
                  <div style={{ marginTop: 6 }}>
                    original content from{" "}
                    <strong><a href="/">xaelserpent.com</a></strong>
                  </div>
                </div>

                <DonationWidget post={post} />

                <AdSlot slot="article-bottom" />

                <RelatedPosts items={related} />
              </article>
            </>
          )}
        </div>
      </main>

      {modal === "about" && <AboutModal onClose={() => setModal(null)} />}
      {modal === "schedule" && <ScheduleModal onClose={() => setModal(null)} />}

      <Footer show />
    </div>
  );
}
